/**
 * TokenGroupCard — one L0 Foundations card.
 *
 * L0 does not live in `registry.components[]`; it lives in the registry's
 * `tokens` object as GROUPS of CSS variable names (surfaces, ink, lines,
 * accent, status, radius, elevation, type, spacing, fonts, motion). The
 * bootstrap browser used to show "L0 · 0 cards", which read as "the design
 * system has no foundations" — the opposite of the truth.
 *
 * Each card shows the group's variables AS THEMSELVES: colours as chips, the
 * type scale at its real sizes, spacing as real widths, radii as real corners,
 * elevation as real shadows. Everything renders inside a token-styled box, so
 * the swatches are the same values the widgets get — not a hand-copied list
 * that can drift.
 */
import { buildTokenStyle } from '../viewir/tokens';

const C = { text: '#1c1c1e', dim: '#6e6e73', border: '#e2e2ea', card: '#fff' };
const L0 = '#8E8E93';

/** How a group wants to be shown. Anything unlisted falls back to `swatch`. */
const GROUP_KIND = {
  surfaces: 'swatch', ink: 'ink', lines: 'line', accent: 'swatch', status: 'swatch',
  radius: 'radius', elevation: 'elevation', type: 'type', spacing: 'spacing',
  fonts: 'font', motion: 'motion',
};

const GROUP_BLURB = {
  surfaces: 'Back-to-front page and card backgrounds.',
  ink: 'Text colours on those surfaces, primary → muted.',
  lines: 'Hairline dividers and borders.',
  accent: 'The ONE signature colour a blueprint may override, plus its tints.',
  status: 'Data semantics only — never decoration.',
  radius: 'Corner rounding, control → hero.',
  elevation: 'Shadow depths.',
  type: 'The type scale. Widgets are a fixed 390px, so these are px, not rem.',
  spacing: 'The 8px rhythm every gap and pad snaps to.',
  fonts: 'Body, display (override for character) and mono.',
  motion: 'Easing curves — gentle ease-out and a slight overshoot.',
};

function Row({ name, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
      <div style={{ flex: '0 0 auto' }}>{children}</div>
      <code style={{
        fontSize: 10.5, color: 'var(--text-3)', whiteSpace: 'nowrap',
        overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0,
      }}>{name}</code>
    </div>
  );
}

function Sample({ kind, name }) {
  const v = `var(${name})`;
  switch (kind) {
    case 'swatch':
      return <span style={{ display: 'block', width: 30, height: 30, borderRadius: 8, background: v, border: '1px solid var(--line)' }} />;
    case 'ink':
      return <span style={{ display: 'block', width: 30, height: 30, borderRadius: 8, background: 'var(--surface)', border: '1px solid var(--line)', color: v, fontSize: 17, fontWeight: 700, textAlign: 'center', lineHeight: '30px' }}>Aa</span>;
    case 'line':
      return <span style={{ display: 'block', width: 30, height: 30, borderRadius: 8, background: 'var(--surface)', borderBottom: `2px solid ${v}` }} />;
    case 'radius':
      return <span style={{ display: 'block', width: 30, height: 30, borderRadius: v, background: 'var(--accent-soft)', border: '1px solid var(--accent-line)' }} />;
    case 'elevation':
      return <span style={{ display: 'block', width: 30, height: 30, borderRadius: 8, background: 'var(--surface)', boxShadow: v }} />;
    case 'type':
      return <span style={{ display: 'block', minWidth: 30, fontSize: v, lineHeight: 1.1, color: 'var(--text)', fontFamily: 'var(--font-display)', fontWeight: 600 }}>Ag</span>;
    case 'spacing':
      return <span style={{ display: 'block', width: v, height: 14, minWidth: 2, borderRadius: 3, background: 'var(--accent)' }} />;
    case 'font':
      return <span style={{ display: 'block', fontFamily: v, fontSize: 15, color: 'var(--text)' }}>Ag 24</span>;
    case 'motion':
    default:
      return <span style={{ display: 'block', width: 30, height: 14, borderRadius: 7, background: 'var(--surface-3)' }} />;
  }
}

export default function TokenGroupCard({ group, names }) {
  const kind = GROUP_KIND[group] || 'swatch';
  const style = buildTokenStyle({ appearance: 'light', accent: '#6E59F2' });
  // The type scale needs vertical room; everything else reads fine in a
  // two-column grid.
  const columns = kind === 'type' || kind === 'font' || kind === 'motion' ? 1 : 2;

  return (
    <article style={{
      background: C.card, border: `1px solid ${C.border}`, borderRadius: 14,
      padding: 16, display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0,
    }}>
      <header style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
            <code style={{ fontSize: 13.5, fontWeight: 700, color: C.text }}>{group}</code>
            <span style={{
              fontSize: 9.5, fontWeight: 800, padding: '1px 6px', borderRadius: 4,
              background: `${L0}1f`, color: L0,
            }}>L0</span>
            <span style={{ fontSize: 10.5, color: C.dim }}>tokens</span>
          </div>
          <div style={{ fontSize: 12, color: C.dim, marginTop: 2 }}>{names.length} variables</div>
        </div>
      </header>

      <div style={{
        ...style, borderRadius: 10, padding: 14,
        display: 'grid', gridTemplateColumns: `repeat(${columns}, minmax(0,1fr))`,
        gap: 10, alignItems: 'center',
      }}>
        {names.map((n) => <Row key={n} name={n}><Sample kind={kind} name={n} /></Row>)}
      </div>

      <p style={{ margin: 0, fontSize: 12, color: C.text, lineHeight: 1.5 }}>
        {GROUP_BLURB[group] || 'Design tokens.'}
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.dim }}>
          Sealed — a widget cannot change these
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          <span style={{
            fontSize: 10.5, padding: '2px 7px', borderRadius: 5,
            background: '#fdf3e3', color: '#B45309', border: '1px solid #f0dfc0',
          }}>
            {group === 'accent' ? 'every token except --accent + --font-display' : 'the whole group'}
          </span>
        </div>
      </div>

      <footer style={{ fontSize: 10.5, color: C.dim, borderTop: `1px solid ${C.border}`, paddingTop: 8 }}>
        mirrored from backend/widget-design-kit/tokens.css
      </footer>
    </article>
  );
}
